import './styles.css'
import React from 'react'
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Popup,
  Segment,
  Transition,
} from 'semantic-ui-react'
import {
  UPDATE_KEG_COLLECTION,
  RESET_EDIT_KEG_COLLECTION_REQUEST_STATUS_STATE,
  SHOW_BOOKINGS_MODAL_ERROR_MESSAGE,
  HIDE_BOOKINGS_MODAL_ERROR_MESSAGE,
} from '../../../../../../redux/actions/actionTypes'
import KegCollectionLocationBtnGroup from '../../shared/KegCollectionLocationBtnGroup/KegCollectionLocationBtnGroup'
import KegCollectionKegQuantitiesInputs from '../../shared/KegCollectionKegQuantitiesInputs'
import KegCollectionAvailableDays from '../../shared/KegCollectionAvailableDays/KegCollectionAvailableDays'
import KegCollectionTimesDropdownGroup from '../../shared/KegCollectionTimesDropdownGroup'
import KegCollectionPickupInstructions from '../../shared/KegCollectionPickupInstructions/KegCollectionPickupInstructions'
import KegCollectionPhoneInput from '../../shared/KegCollectionPhoneInput'
import ModalErrorBanner from '../../shared/BookingsModalErrorBanner/index'
import {
  hourToLocalizedLabel,
  isHour,
  toNum,
  HOURS_MIN,
  HOURS_MAX,
  numericInputRegex,
  DAY_OPTIONS,
} from '../../../utils/kegCollectionFormUtils'
import useKegCollectionErrorState from '../../../utils/useKegCollectionErrorState'
import { ABBREVIATED_DAYS_LOCALIZED_STRINGS } from '../../../../../../constants'
import { sanitizedInput } from '../../../../../../utils/formNormalization'

function KegCollectionEditModal(props) {
  const {
    kegCollectionObj,
    kegCollectionQuantities: { L20, L30, G9, L50 },
    formatDate,
    dateFormatter,
    dispatch,
    createAction,
    editKegCollectionRequestStatusState: { isEditing, didEditSucceed, didEditFail },
    translate,
  } = props

  const [openEditModal, setOpenEditModal] = React.useState(false)

  React.useEffect(() => {
    if (didEditSucceed) {
      setOpenEditModal(false)
    }
  }, [didEditSucceed, dispatch, createAction, translate])

  React.useEffect(() => {
    if (didEditFail) {
      dispatch(
        createAction(SHOW_BOOKINGS_MODAL_ERROR_MESSAGE, {
          header: translate('Error'),
          content: translate('kegCollectionUpdateErrorMessage'),
        })
      )
    }
  }, [didEditFail, dispatch, createAction, translate])

  const [mounted, setMounted] = React.useState(false)

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (openEditModal) {
      const t = setTimeout(() => setMounted(true), 80)
      return () => clearTimeout(t)
    }
    setMounted(false)
  }, [openEditModal])

  const buildInitialFormValues = React.useCallback(
    () => ({
      L20,
      L30,
      G9,
      L50,
      kcRequestItemQtyObjectSet: kegCollectionObj?.kcRequestItemQtyObjectSet || [],
      kegLocation: kegCollectionObj?.kegLocation || '',
      pickupInstruction: kegCollectionObj?.pickupInstruction || '',
      phoneNumber: kegCollectionObj?.phoneNumber || '',
      availableDays: {
        monday: !!kegCollectionObj?.venueOpenMonday,
        tuesday: !!kegCollectionObj?.venueOpenTuesday,
        wednesday: !!kegCollectionObj?.venueOpenWednesday,
        thursday: !!kegCollectionObj?.venueOpenThursday,
        friday: !!kegCollectionObj?.venueOpenFriday,
      },
      startHour: isHour(kegCollectionObj?.startHour) ? kegCollectionObj.startHour : HOURS_MIN,
      endHour: isHour(kegCollectionObj?.endHour) ? kegCollectionObj.endHour : HOURS_MAX,
      updateVenueCollectionInfo: kegCollectionObj?.updateVenueCollectionInfo,
    }),
    [L20, L30, G9, L50, kegCollectionObj]
  )

  const [formValues, setFormValues] = React.useState(buildInitialFormValues())
  const { validationErrors } = useKegCollectionErrorState(formValues)
  const hasErrors = React.useMemo(
    () => Object.values(validationErrors).some(Boolean),
    [validationErrors]
  )

  React.useEffect(() => {
    // Reset form values each time modal is opened
    if (!openEditModal) return
    setFormValues(buildInitialFormValues())
  }, [openEditModal, buildInitialFormValues])

  const baselineRef = React.useRef(null)
  React.useEffect(() => {
    if (!openEditModal) return
    baselineRef.current = {
      L20: toNum(L20),
      L30: toNum(L30),
      G9: toNum(G9),
      L50: toNum(L50),
      kcRequestItemQtyObjectSet: kegCollectionObj?.kcRequestItemQtyObjectSet || [],
      availableDays: {
        monday: !!kegCollectionObj?.venueOpenMonday,
        tuesday: !!kegCollectionObj?.venueOpenTuesday,
        wednesday: !!kegCollectionObj?.venueOpenWednesday,
        thursday: !!kegCollectionObj?.venueOpenThursday,
        friday: !!kegCollectionObj?.venueOpenFriday,
      },
      kegLocation: kegCollectionObj?.kegLocation || '',
      pickupInstruction: kegCollectionObj?.pickupInstruction || '',
      phoneNumber: kegCollectionObj?.phoneNumber || '',
      startHour: isHour(kegCollectionObj?.startHour) ? kegCollectionObj.startHour : HOURS_MIN,
      endHour: isHour(kegCollectionObj?.endHour) ? kegCollectionObj.endHour : HOURS_MAX,
    }
  }, [openEditModal, L20, L30, G9, L50, kegCollectionObj])

  const isDirty = React.useMemo(() => {
    if (!baselineRef.current) return false
    const base = baselineRef.current
    const cur = formValues
    const daysChanged = DAY_OPTIONS.some(d => base.availableDays[d] !== cur.availableDays[d])

    return (
      toNum(base.L20) !== toNum(cur.L20) ||
      toNum(base.L30) !== toNum(cur.L30) ||
      toNum(base.G9) !== toNum(cur.G9) ||
      toNum(base.L50) !== toNum(cur.L50) ||
      base.kegLocation !== cur.kegLocation ||
      base.pickupInstruction !== cur.pickupInstruction ||
      base.phoneNumber !== cur.phoneNumber ||
      base.startHour !== cur.startHour ||
      base.endHour !== cur.endHour ||
      daysChanged
    )
  }, [formValues])

  const getCollectionDaysLabel = React.useCallback(() => {
    const days = Object.keys(formValues.availableDays).filter(day => formValues.availableDays[day])
    const abbrevs = []

    ABBREVIATED_DAYS_LOCALIZED_STRINGS.forEach(day => {
      const found = days.find(d => d.toLowerCase() === Object.keys(day)[0])
      if (found) {
        abbrevs.push(day[found])
      }
    })
    if (abbrevs.length === 0) {
      return (
        <span
          style={{
            color: '#912d2b',
            fontWeight: 'bold',
          }}
        >
          {translate('noDaysSelected')}
        </span>
      )
    }
    return abbrevs.map((d, index) => (
      <span key={d}>
        {translate(d)}
        {index === abbrevs.length - 1 ? '' : ', '}
      </span>
    ))
  }, [formValues.availableDays, translate])

  const getTotalKegQuantityLabel = React.useCallback(() => {
    const L20Num = toNum(formValues.L20)
    const L30Num = toNum(formValues.L30)
    const G9Num = toNum(formValues.G9)
    const L50Num = toNum(formValues.L50)
    const total = L20Num + L30Num + G9Num + L50Num
    return `${translate('totalQuantity')}: ${total}`
  }, [formValues, translate])

  const setField = React.useCallback((field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleQtyChange = React.useCallback(
    e => {
      const { name, value } = e.target
      if (value.length > 3 || !numericInputRegex.test(value)) return
      setFormValues(prev => ({
        ...prev,
        kcRequestItemQtyObjectSet: prev.kcRequestItemQtyObjectSet.map(kc => {
          if (kc.containerType === name) {
            return { ...kc, quantity: value === '' ? 0 : Number(value) }
          }
          return kc
        }),
        [name]: value === '' ? '' : Number(value),
      }))
    },
    [setFormValues]
  )

  const handleAvailableDaysChange = React.useCallback(
    (day, checked) => {
      setField('availableDays', { ...formValues.availableDays, [day]: !!checked })
    },
    [formValues.availableDays, setField]
  )

  const handleCollectionLocationChange = React.useCallback(
    location => {
      setField('kegLocation', location)
    },
    [setField]
  )

  const handlePickupInstructionChange = React.useCallback(
    value => {
      setField('pickupInstruction', sanitizedInput(value))
    },
    [setField]
  )

  const handlePhoneNumberChange = React.useCallback(
    value => {
      setField('phoneNumber', value || '')
    },
    [setField]
  )

  const handleAvailableTimesChange = React.useCallback(
    (field, value) => {
      setField(field, value)
    },
    [setField]
  )

  const handleCheckedUpdateCollectionInfo = React.useCallback(
    checked => {
      setField('updateVenueCollectionInfo', !!checked)
    },
    [setField]
  )

  const handleSaveEditKegCollection = React.useCallback(
    collectionRequest => {
      if (hasErrors) return

      const updatedKegCollectionObj = {
        addressId: collectionRequest.addressId,
        createdBy: collectionRequest.createdBy,
        customerId: collectionRequest.customerId,
        customerName: collectionRequest.customerName,
        customerTapId: collectionRequest.customerTapId,
        dateCreated: collectionRequest.dateCreated,
        endHour: formValues.endHour,
        fullAddress: collectionRequest.fullAddress,
        kcRequestItemQtyObjectSet: formValues.kcRequestItemQtyObjectSet,
        kegLocation: formValues.kegLocation,
        phoneNumber: formValues.phoneNumber,
        pickupInstruction: formValues.pickupInstruction,
        startHour: formValues.startHour,
        updateVenueCollectionInfo: formValues.updateVenueCollectionInfo,
        venueOpenMonday: formValues.availableDays.monday,
        venueOpenTuesday: formValues.availableDays.tuesday,
        venueOpenWednesday: formValues.availableDays.wednesday,
        venueOpenThursday: formValues.availableDays.thursday,
        venueOpenFriday: formValues.availableDays.friday,
      }

      dispatch(
        createAction(UPDATE_KEG_COLLECTION, {
          kegCollectionId: collectionRequest.id,
          updatedKegCollectionParams: updatedKegCollectionObj,
        })
      )
    },
    [createAction, dispatch, formValues, hasErrors]
  )

  return (
    <Modal
      open={openEditModal}
      onClose={() => setOpenEditModal(false)}
      onOpen={() => setOpenEditModal(true)}
      trigger={
        <button type="button" className="kegCollectionTableRowBtn kegCollectionBtnEdit">
          <Icon name="pencil" style={{ marginBottom: 3 }} /> {translate('Edit')}
        </button>
      }
      size="large"
      closeOnDimmerClick={false}
      closeOnEscape
    >
      <ModalErrorBanner />
      <ModalHeader>{translate('kegCollectionEditModalHeader')}</ModalHeader>
      <Transition visible={mounted} animation="fade up" duration={300}>
        <ModalContent scrolling>
          <Form>
            {hasErrors && (
              <Message
                negative
                size="small"
                icon="exclamation triangle"
                header={translate('fixHighlightedFields')}
                list={[
                  validationErrors.startHour,
                  validationErrors.endHour,
                  validationErrors.availableDays,
                  validationErrors.phoneNumber,
                  validationErrors.kcRequestItemQtyObjectSet,
                ].filter(Boolean)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Transition visible={mounted} animation="fade" duration={250}>
              <Header
                as="h3"
                content={kegCollectionObj?.customerName}
                subheader={translate('editDetailsBelow')}
              />
            </Transition>

            <Transition visible={mounted} animation="fade" duration={250}>
              <div id="kegCollectionEditModalVenueDetails">
                <div id="kegCollectionEditModalVenueName">
                  <Icon name="map marker" style={{ marginBottom: 4, marginRight: 8 }} />
                  <span>{` ${kegCollectionObj?.fullAddress || ''}`}</span>
                </div>

                <div id="kegCollectionEditModalDateCreated">
                  <Icon
                    name="calendar alternate outline"
                    style={{ marginBottom: 4, marginRight: 8 }}
                  />
                  <span>{formatDate(kegCollectionObj?.dateCreated, dateFormatter, true)}</span>
                </div>
              </div>
            </Transition>

            <Transition visible={mounted} animation="fade up" duration={300}>
              <Label.Group style={{ marginBottom: 10 }}>
                <Label basic size="large">
                  <Icon name="calendar outline" />
                  {getCollectionDaysLabel()}
                </Label>
                <Label
                  basic
                  size="large"
                  style={{
                    backgroundColor:
                      validationErrors.startHour || validationErrors.endHour
                        ? '#fff6f6'
                        : undefined,
                    boxShadow:
                      validationErrors.startHour || validationErrors.endHour
                        ? '0 0 0 1px #e0b4b4 inset,0 0 0 0 transparent'
                        : undefined,
                  }}
                >
                  <Icon name="clock outline" />
                  {hourToLocalizedLabel(formValues.startHour)} –{' '}
                  {hourToLocalizedLabel(formValues.endHour)}
                </Label>
                <Label basic size="large" color={formValues.kegLocation ? 'teal' : null}>
                  <Icon name="building" />
                  {translate(formValues.kegLocation) || '-'}
                </Label>
              </Label.Group>
            </Transition>

            <Transition visible={mounted} animation="fade up" duration={350}>
              <Grid stackable columns={2}>
                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 210 }}>
                      <Header
                        as="h5"
                        content={translate('kegQuantities')}
                        subheader={getTotalKegQuantityLabel()}
                      />
                      <KegCollectionKegQuantitiesInputs
                        formValues={formValues}
                        onChange={handleQtyChange}
                        isDisabled={isEditing}
                        validationErrors={validationErrors}
                        focusFirstInput={mounted}
                      />
                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 210 }}>
                      <Header
                        as="h5"
                        content={translate('availableCollectionDays')}
                        style={{ color: validationErrors.availableDays ? '#9f3a38' : undefined }}
                      />
                      <KegCollectionAvailableDays
                        days={DAY_OPTIONS}
                        formValues={formValues}
                        isDisabled={isEditing}
                        onClick={handleAvailableDaysChange}
                        validationErrors={validationErrors}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 200 }}>
                      <Header as="h5" content={translate('availableCollectionTimes')} />
                      <KegCollectionTimesDropdownGroup
                        formValues={formValues}
                        onChange={handleAvailableTimesChange}
                        validationErrors={validationErrors}
                        isDisabled={isEditing}
                      />
                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 200 }}>
                      <Header as="h5" content={translate('kegLocation')} />
                      <KegCollectionLocationBtnGroup
                        onClick={handleCollectionLocationChange}
                        isDisabled={isEditing}
                        formValues={formValues}
                        validationErrors={validationErrors}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 200 }}>
                      <Header as="h5" content={translate('contactPhoneNumber')} />
                      <KegCollectionPhoneInput
                        formValues={formValues}
                        onChange={handlePhoneNumberChange}
                        validationErrors={validationErrors}
                        isDisabled={isEditing}
                        selectedCountry={kegCollectionObj?.addressObject?.country || ''}
                      />
                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 165 }}>
                      <Header as="h5" content={translate('pickupInstructions')} />
                      <KegCollectionPickupInstructions
                        onChange={handlePickupInstructionChange}
                        formValues={formValues}
                        isDisabled={isEditing}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Transition>

            <Divider hidden />
          </Form>
        </ModalContent>
      </Transition>

      <ModalActions>
        <Popup
          content={translate('updateVenueCollectionInfoTooltip')}
          trigger={
            <Icon name="info circle" aria-label={translate('updateVenueCollectionInfoTooltip')} />
          }
          disabled={!isDirty || isEditing || hasErrors}
        />
        <Checkbox
          label={translate('updateVenueCollectionInfo')}
          checked={formValues.updateVenueCollectionInfo}
          onChange={(_, { checked }) => handleCheckedUpdateCollectionInfo(checked)}
          disabled={!isDirty || isEditing || hasErrors}
        />
        <Button
          onClick={() => {
            setOpenEditModal(false)
            dispatch(createAction(RESET_EDIT_KEG_COLLECTION_REQUEST_STATUS_STATE))
            dispatch(createAction(HIDE_BOOKINGS_MODAL_ERROR_MESSAGE))
          }}
          disabled={isEditing}
        >
          <Icon name="times" /> {translate('Cancel')}
        </Button>
        <Button
          positive
          onClick={() => handleSaveEditKegCollection(kegCollectionObj)}
          loading={isEditing}
          disabled={!isDirty || isEditing || hasErrors}
        >
          <Icon name="save" />
          {translate('saveChangesBtnLabel')}
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default React.memo(KegCollectionEditModal)
